import React from 'react';
import { styles } from './PromoCard.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useTrackingContext } from '../Tracking';
import { trackingDataToDataAttributes } from '../HeroBanner/utils';
import { YouTubePlayer, YouTubePlayerProps } from '../YouTubePlayer/YouTubePlayer';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { useNavigate } from '../PlatformFunctionality';
import { shuffleList } from '@jbhi-fi/utils/src/shuffleList/shuffleList';
import { getSentenceCase } from '@jbhi-fi/utils/src/getSentenceCase/getSentenceCase';

export type PromoCardProps = {
  isHidden?: boolean;
  image?: React.ReactNode;
  youTubeProps?: YouTubePlayerProps;
  href: string;
  isContained?: boolean;
  trackingLabel?: string;
  isDraft?: boolean;
  trackingData?: { id: string; isBranded?: boolean };
  cardFooter?: {
    subText: string;
    buttonLabel: string;
    brandLogo?: IconName;
  };
};

export const PromoCard = ({
  image,
  href,
  isContained,
  trackingLabel,
  isHidden,
  trackingData,
  youTubeProps,
  cardFooter,
}: PromoCardProps) => {
  const { tracker } = useTrackingContext();
  const navigate = useNavigate();

  if (isHidden) return null;

  const iconName = cardFooter?.brandLogo ?? null;

  const handleClick = () => {
    tracker.trackClick({ label: trackingLabel, extraData: { moduleName: 'Promo Card' } });
  };

  const hrefLink = !cardFooter && image ? href : undefined;

  return (
    <a
      className={classNames(styles.container, styles.containerVariants({ isContained }))}
      href={hrefLink}
      onClick={handleClick}
      {...trackingDataToDataAttributes(trackingData)}
    >
      <div className={styles.innerContainer({ hasFooter: !!cardFooter })}>
        {image ? image : null}
        {youTubeProps ? (
          <div className={styles.youTubeWrapper}>
            <YouTubePlayer {...youTubeProps} orientation="Portrait" classes={styles.promoYouTubeContainer} />
          </div>
        ) : null}
        {cardFooter ? (
          <div className={styles.cardFooterSection}>
            {iconName ? <Icon name={iconName} className={styles.brandLogo} /> : null}
            <div className={styles.cardFooter}>
              <div className={styles.footerSubText}>{cardFooter.subText}</div>
              <div className={styles.footerButton}>
                <Button
                  type="primary"
                  onClick={() => {
                    navigate(href);
                  }}
                  size="sm"
                  classes={styles.buttonLabel}
                  data-testid="promo-card-footer-button"
                >
                  {getSentenceCase(cardFooter.buttonLabel)}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </a>
  );
};

export type PromoCardCarouselProps = {
  cards: Array<PromoCardProps>;
  lockedCardCount?: number;
};

export const PromoCardCarousel = ({ cards, lockedCardCount }: PromoCardCarouselProps) => {
  const isMobile = !useBreakpoint('md', 'up');
  const isDesktop = useBreakpoint('lg', 'up');
  const visibleCards = cards.filter(card => !card.isHidden && !card.isDraft);

  const isPreview = window.Jbhifi?.isContentPreview || false;
  const shouldShuffle = lockedCardCount != undefined && !isPreview;
  const promoCards = shouldShuffle ? shuffleList(visibleCards, lockedCardCount) : visibleCards;

  const nonHiddenCardCount = visibleCards.length;

  return (
    <div className={styles.carouselWrapper}>
      <Carousel
        slidesPerView="auto"
        showPagination={false}
        showProgressBar={isMobile || !isDesktop || nonHiddenCardCount > 3}
        showArrows={true}
        fullBleed={isMobile}
        spaceBetween={isDesktop && nonHiddenCardCount <= 3 ? 43 : 16}
        arrowPosition="bottom"
        slideArrowClassName={styles.slideArrow}
        rewind
      >
        {promoCards.map((cardProps, i) => (
          <PromoCard {...cardProps} key={`${cardProps.href}-${i}-${cardProps.youTubeProps?.videoUrl || 'no-video'}`} />
        ))}
      </Carousel>
    </div>
  );
};
